import { PlusOutlined } from "@ant-design/icons";
import { faChildReaching, faPersonCane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Col, Row, Tabs, TabsProps, Typography } from "antd";
import SkeletonButton from "antd/lib/skeleton/Button";
import axios from "axios";
import SearchBar from "components/SearchBar/SearchBar";
import { BLgroup } from "interfaces/internal";
import { Member } from "interfaces/users";
import React, { useEffect, useState } from "react";
import { NAVBAR_HEIGHT } from "components/NavAdmin/AdminNavbar";
import EditMember from "./EditMember";
import Groups from "./Groups/Groups";
import { SingleList } from "./Lists";
import SelectNewMember from "./NewMember";

const Members = () => {
  const { Title } = Typography;
  const [searchTerm, setSearchTerm] = useState("");
  const handleSearch = (newSearchTerm: React.SetStateAction<string>) => {
    setSearchTerm(newSearchTerm);
  };
  const [loadingAllMembers, setLoadingAllMembers] = useState<boolean>(true);

  const [blGroups, setBlGroups] = useState<BLgroup[]>([]);
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [addNewMember, setAddNewMember] = useState<boolean>(false);

  useEffect(() => {
    if (selectedMember) {
      const editMemberElement = document.getElementById("edit-member");
      if (editMemberElement) {
        const yOffset = -NAVBAR_HEIGHT; // Adjust for navbar height
        const y = editMemberElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  }, [selectedMember]);

  useEffect(() => {
    axios
      .get<Member[]>("/internal/members/")
      .then(response => {
        setAllMembers(response.data);
        setLoadingAllMembers(false);
      })
      .catch(() => setLoadingAllMembers(false));
  }, []);

  useEffect(() => {
    axios.get<BLgroup[]>("/internal/groups/").then(response => {
      setBlGroups(response.data);
    });
  }, []);

  const getFilteredMembers = () =>
    allMembers.filter(member => {
      if (searchTerm === "") {
        return member;
      }
      if (member.full_name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return member;
      }
      return false;
    });

  const filteredMembers =
    allMembers &&
    getFilteredMembers().map(member => {
      return member;
    });

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: `Aktive`, // Medlemmer som er aktive, og på BL-sidene
      children: (
        <SingleList
          dataSource={filteredMembers
            .filter(member => member.is_active_member && member.show_on_bl_page)
            .sort((a, b) => a.full_name.localeCompare(b.full_name))}
          loading={loadingAllMembers}
          onSelectMember={(member: Member) => setSelectedMember(member)}
          groups={blGroups}
        />
      ),
    },
    {
      key: "2",
      label: `Aktive seniorer`, // Medlemmer som er aktive, men ikke på BL-sidene
      children: (
        <SingleList
          dataSource={filteredMembers
            .filter(member => member.is_active_member && !member.show_on_bl_page)
            .sort((a, b) => a.full_name.localeCompare(b.full_name))}
          loading={loadingAllMembers}
          onSelectMember={(member: Member) => setSelectedMember(member)}
          groups={blGroups}
        />
      ),
    },
    {
      key: "3",
      label: `Seniorer`,
      children: (
        <SingleList
          // Medlemmer som ikke er aktive
          dataSource={filteredMembers
            .filter(member => !member.is_active_member)
            .sort((a, b) => a.full_name.localeCompare(b.full_name))}
          loading={loadingAllMembers}
          onSelectMember={(member: Member) => setSelectedMember(member)}
        />
      ),
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col lg={12} sm={24}>
          <Card>
            <SelectNewMember
              isOpen={addNewMember}
              onClose={() => setAddNewMember(false)}
              onSave={m => setAllMembers([...allMembers, m])}
              existingMembers={allMembers}
            />
            <Row justify="space-between" align="middle">
              <Title level={2}>Bindeleddere</Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddNewMember(true)}>
                Legg til nytt medlem
              </Button>
            </Row>
            <Row style={{ margin: "25px auto", width: "100%" }}>
              <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
            </Row>
            <Tabs defaultActiveKey="1" items={items} />
          </Card>
        </Col>
        <Col lg={12} sm={24}>
          <Row gutter={16}>
            <Col span={12}>
              <Card>
                <Title level={5}>Aktive</Title>
                <Row gutter={10}>
                  <Col>
                    <FontAwesomeIcon
                      icon={faChildReaching}
                      size="2x"
                      style={{
                        backgroundColor: "var(--primary-color)",
                        color: "var(--light)",
                        padding: "15px",
                        borderRadius: "var(--border-radius)",
                        aspectRatio: "1/1",
                      }}
                    />
                  </Col>
                  <Col>
                    <Title level={5} style={{ margin: 0, padding: 0 }}>
                      ANTALL
                    </Title>
                    <Title level={3} style={{ margin: 0, padding: 0 }}>
                      {loadingAllMembers ? (
                        <SkeletonButton />
                      ) : (
                        allMembers.filter(member => member.is_active_member && member.show_on_bl_page).length
                      )}
                    </Title>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Title level={5}>Seniorer</Title>
                <Row gutter={10}>
                  <Col>
                    <FontAwesomeIcon
                      icon={faPersonCane}
                      size="2x"
                      style={{
                        backgroundColor: "var(--primary-color)",
                        color: "var(--light)",
                        padding: "15px",
                        borderRadius: "var(--border-radius)",
                        aspectRatio: "1/1",
                      }}
                    />
                  </Col>
                  <Col>
                    <Title level={5} style={{ margin: 0, padding: 0 }}>
                      ANTALL
                    </Title>
                    <Title level={3} style={{ margin: 0, padding: 0 }}>
                      {loadingAllMembers ? (
                        <SkeletonButton />
                      ) : (
                        allMembers.length -
                        allMembers.filter(member => member.show_on_bl_page && member.is_active_member).length
                      )}
                    </Title>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          {selectedMember && (
            <EditMember
              member={selectedMember}
              onClose={() => setSelectedMember(null)}
              onEdit={updateMember =>
                setAllMembers(allMembers.map(member => (member.id === updateMember.id ? updateMember : member)))
              }
              onDeleted={id => setAllMembers(allMembers.filter(member => member.id !== id))}
            />
          )}
          <Row gutter={24} style={{ paddingLeft: "8px", paddingTop: "16px" }}>
            <Col span={24}>
              <Groups blGroups={blGroups} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Members;
